/* eslint-disable no-unknown/no-unknown, no-raw-nulls/no-raw-nulls */
// @services/clients/StatementClassifierClient.ts
import * as E from "fp-ts/Either";
import { pipe } from "fp-ts/function";
import * as t from "io-ts";
import { PathReporter } from "io-ts/PathReporter";

import type { OptionTs, TaskEitherTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";

interface StatementClassifierJobIdBrand {
    readonly StatementClassifierJobId: unique symbol;
}

interface StatementClassifierGuidanceTextBrand {
    readonly StatementClassifierGuidanceText: unique symbol;
}

interface StatementClassifierGuidanceSectionTitleBrand {
    readonly StatementClassifierGuidanceSectionTitle: unique symbol;
}

interface StatementClassifierGuidanceSectionBodyBrand {
    readonly StatementClassifierGuidanceSectionBody: unique symbol;
}

interface StatementClassifierFileNameBrand {
    readonly StatementClassifierFileName: unique symbol;
}

interface StatementClassifierMediaTypeBrand {
    readonly StatementClassifierMediaType: unique symbol;
}

interface StatementClassifierTraceIdBrand {
    readonly StatementClassifierTraceId: unique symbol;
}

interface StatementClassifierArtifactIdBrand {
    readonly StatementClassifierArtifactId: unique symbol;
}

interface StatementClassifierDateBrand {
    readonly StatementClassifierDate: unique symbol;
}

interface StatementClassifierDescriptionBrand {
    readonly StatementClassifierDescription: unique symbol;
}

interface StatementClassifierClassificationBrand {
    readonly StatementClassifierClassification: unique symbol;
}

interface StatementClassifierReasoningBrand {
    readonly StatementClassifierReasoning: unique symbol;
}

interface StatementClassifierFailureMessageBrand {
    readonly StatementClassifierFailureMessage: unique symbol;
}

interface StatementClassifierWorkbookFileNameBrand {
    readonly StatementClassifierWorkbookFileName: unique symbol;
}

interface StatementClassifierFileSizeBrand {
    readonly StatementClassifierFileSize: unique symbol;
}

interface StatementClassifierAmountBrand {
    readonly StatementClassifierAmount: unique symbol;
}

interface StatementClassifierConfidenceBrand {
    readonly StatementClassifierConfidence: unique symbol;
}

const nonEmptyString = (value: string): boolean => value.trim().length > 0;
const nonNegativeInteger = (value: number): boolean => Number.isInteger(value) && value >= 0;
const finiteNumber = (value: number): boolean => Number.isFinite(value);
const confidenceNumber = (value: number): boolean => Number.isFinite(value) && value >= 0 && value <= 1;

const jobIdCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierJobIdBrand> => nonEmptyString(value),
    "StatementClassifierJobId",
);
const guidanceTextCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierGuidanceTextBrand> => nonEmptyString(value),
    "StatementClassifierGuidanceText",
);
const guidanceSectionTitleCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierGuidanceSectionTitleBrand> => nonEmptyString(value),
    "StatementClassifierGuidanceSectionTitle",
);
const guidanceSectionBodyCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierGuidanceSectionBodyBrand> => nonEmptyString(value),
    "StatementClassifierGuidanceSectionBody",
);
const fileNameCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierFileNameBrand> => nonEmptyString(value),
    "StatementClassifierFileName",
);
const mediaTypeCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierMediaTypeBrand> => nonEmptyString(value),
    "StatementClassifierMediaType",
);
const traceIdCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierTraceIdBrand> => nonEmptyString(value),
    "StatementClassifierTraceId",
);
const artifactIdCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierArtifactIdBrand> => nonEmptyString(value),
    "StatementClassifierArtifactId",
);
const dateCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierDateBrand> => /^\d{4}-\d{2}-\d{2}$/.test(value),
    "StatementClassifierDate",
);
const descriptionCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierDescriptionBrand> => nonEmptyString(value),
    "StatementClassifierDescription",
);
const classificationCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierClassificationBrand> => nonEmptyString(value),
    "StatementClassifierClassification",
);
const reasoningCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierReasoningBrand> => nonEmptyString(value),
    "StatementClassifierReasoning",
);
const failureMessageCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierFailureMessageBrand> => nonEmptyString(value),
    "StatementClassifierFailureMessage",
);
const refusalMessageCodec = t.string;
const workbookFileNameCodec = t.brand(
    t.string,
    (value): value is t.Branded<string, StatementClassifierWorkbookFileNameBrand> =>
        nonEmptyString(value) && value.endsWith(".xlsx"),
    "StatementClassifierWorkbookFileName",
);
const fileSizeCodec = t.brand(
    t.number,
    (value): value is t.Branded<number, StatementClassifierFileSizeBrand> => nonNegativeInteger(value),
    "StatementClassifierFileSize",
);
const amountCodec = t.brand(
    t.number,
    (value): value is t.Branded<number, StatementClassifierAmountBrand> => finiteNumber(value),
    "StatementClassifierAmount",
);
const confidenceCodec = t.brand(
    t.number,
    (value): value is t.Branded<number, StatementClassifierConfidenceBrand> => confidenceNumber(value),
    "StatementClassifierConfidence",
);

const nullable = <A>(codec: t.Decoder<unknown, A>): t.Type<OptionTs<A>> =>
    new t.Type<OptionTs<A>>(
        "NullableOption",
        (value): value is OptionTs<A> => value === Option.none<A>(),
        (value, _context) =>
            value === null
                ? t.success(Option.none())
                : pipe(
                      codec.decode(value),
                      E.map((decoded) => Option.some(decoded)),
                  ),
        (value: OptionTs<A>) => value,
    );

export type StatementClassifierJobId = t.TypeOf<typeof jobIdCodec>;
export type StatementClassifierGuidanceText = t.TypeOf<typeof guidanceTextCodec>;
export type StatementClassifierGuidanceSectionTitle = t.TypeOf<typeof guidanceSectionTitleCodec>;
export type StatementClassifierGuidanceSectionBody = t.TypeOf<typeof guidanceSectionBodyCodec>;
export type StatementClassifierFileName = t.TypeOf<typeof fileNameCodec>;
export type StatementClassifierMediaType = t.TypeOf<typeof mediaTypeCodec>;
export type StatementClassifierTraceId = t.TypeOf<typeof traceIdCodec>;
export type StatementClassifierArtifactId = t.TypeOf<typeof artifactIdCodec>;
export type StatementClassifierDate = t.TypeOf<typeof dateCodec>;
export type StatementClassifierDescription = t.TypeOf<typeof descriptionCodec>;
export type StatementClassifierClassification = t.TypeOf<typeof classificationCodec>;
export type StatementClassifierReasoning = t.TypeOf<typeof reasoningCodec>;
export type StatementClassifierFailureMessage = t.TypeOf<typeof failureMessageCodec>;
export type StatementClassifierRefusalMessage = string;
export type StatementClassifierWorkbookFileName = t.TypeOf<typeof workbookFileNameCodec>;
export type StatementClassifierFileSize = t.TypeOf<typeof fileSizeCodec>;
export type StatementClassifierAmount = t.TypeOf<typeof amountCodec>;
export type StatementClassifierConfidence = t.TypeOf<typeof confidenceCodec>;

export type StatementClassifierJobPhase =
    | "Accepted"
    | "Staging"
    | "Extracting"
    | "Classifying"
    | "WritingWorkbook"
    | "Completed"
    | "Failed";

export type StatementClassifierErrorCode =
    | "invalidPayload"
    | "invalidJobId"
    | "unknownJob"
    | "workbookUnavailable"
    | "serviceUnavailable"
    | "unsupportedFileType"
    | "noExtractableText"
    | "processingFailed"
    | "downloadUnavailable"
    | "networkError"
    | "responseDecodeError";

export type StatementClassifierApiDecodeResult<T> =
    | {
          readonly type: "ok";
          readonly value: T;
      }
    | {
          readonly type: "error";
          readonly message: string;
      };

export type StatementClassifierGuidanceSection = {
    readonly title: StatementClassifierGuidanceSectionTitle;
    readonly body: StatementClassifierGuidanceSectionBody;
};

export type StatementClassifierGuidance = {
    readonly text: StatementClassifierGuidanceText;
    readonly businessDescription: string;
    readonly businessExamples: string;
    readonly personalExamples: string;
    readonly ambiguousRules: string;
    readonly extraNotes: string;
    readonly classificationAdjustments: string;
    readonly sections: OptionTs<readonly StatementClassifierGuidanceSection[]>;
};

export type StatementClassifierSubmittedFile = {
    readonly name: StatementClassifierFileName;
    readonly size: StatementClassifierFileSize;
    readonly type: StatementClassifierMediaType;
    readonly eligible: boolean;
};

export type StatementClassifierSubmitJobRequest = {
    readonly files: File[];
    readonly guidance: StatementClassifierGuidance;
};

export type StatementClassifierSubmitWireFile = {
    readonly fileName: StatementClassifierFileName;
    readonly mediaType: StatementClassifierMediaType;
    readonly byteSize: StatementClassifierFileSize;
    readonly bytesBase64: string;
};

export type StatementClassifierSubmitWireRequest = {
    readonly guidance: StatementClassifierGuidanceText;
    readonly businessDescription: string;
    readonly businessExamples: string;
    readonly personalExamples: string;
    readonly ambiguousRules: string;
    readonly extraNotes: string;
    readonly classificationAdjustments: string;
    readonly files: readonly StatementClassifierSubmitWireFile[];
};

export type StatementClassifierTrace = {
    readonly traceId: StatementClassifierTraceId;
};

export type StatementClassifierJobCounts = {
    readonly submittedFiles: number;
    readonly previewRows: number;
};

export type StatementClassifierArtifact = {
    readonly id: StatementClassifierArtifactId;
    readonly kind: "workbook" | string;
    readonly fileName: OptionTs<StatementClassifierWorkbookFileName>;
    readonly mediaType: OptionTs<StatementClassifierMediaType>;
    readonly byteSize: OptionTs<StatementClassifierFileSize>;
};

export type StatementClassifierJobStatus = {
    readonly jobId: StatementClassifierJobId;
    readonly phase: StatementClassifierJobPhase;
    readonly failure: OptionTs<StatementClassifierFailureMessage>;
    readonly counts: OptionTs<StatementClassifierJobCounts>;
    readonly workbook: OptionTs<StatementClassifierArtifact>;
};

export type StatementClassifierSubmitJobResult = {
    readonly jobId: StatementClassifierJobId;
    readonly status: StatementClassifierJobStatus;
    readonly trace: StatementClassifierTrace;
};

export type StatementClassifierStatusResult = {
    readonly status: StatementClassifierJobStatus;
    readonly trace: StatementClassifierTrace;
};

export type StatementClassifierPreviewRow = {
    readonly fileName: StatementClassifierFileName;
    readonly date: StatementClassifierDate;
    readonly description: StatementClassifierDescription;
    readonly amount: OptionTs<StatementClassifierAmount>;
    readonly classification: StatementClassifierClassification;
    readonly confidence: StatementClassifierConfidence;
    readonly reasoning: OptionTs<StatementClassifierReasoning>;
};

export type StatementClassifierPreviewResult = {
    readonly rows: readonly StatementClassifierPreviewRow[];
    readonly trace: StatementClassifierTrace;
};

export type StatementClassifierWorkbookResult = {
    readonly jobId: StatementClassifierJobId;
    readonly fileName: StatementClassifierWorkbookFileName;
    readonly content: Blob;
};

export type StatementClassifierClientError = {
    readonly error: StatementClassifierErrorCode;
    readonly message: StatementClassifierRefusalMessage;
    readonly trace: OptionTs<StatementClassifierTrace>;
};

const guidanceSectionCodec = t.type({
    title: guidanceSectionTitleCodec,
    body: guidanceSectionBodyCodec,
});

const guidanceCodec = t.type({
    text: guidanceTextCodec,
    businessDescription: t.string,
    businessExamples: t.string,
    personalExamples: t.string,
    ambiguousRules: t.string,
    extraNotes: t.string,
    classificationAdjustments: t.string,
    sections: nullable(t.readonlyArray(guidanceSectionCodec)),
});

const submittedFileCodec = t.type({
    name: fileNameCodec,
    size: fileSizeCodec,
    type: mediaTypeCodec,
    eligible: t.boolean,
});

const submitWireFileCodec = t.type({
    fileName: fileNameCodec,
    mediaType: mediaTypeCodec,
    byteSize: fileSizeCodec,
    bytesBase64: t.string,
});

const submitWireRequestCodec = t.type({
    guidance: guidanceTextCodec,
    businessDescription: t.string,
    businessExamples: t.string,
    personalExamples: t.string,
    ambiguousRules: t.string,
    extraNotes: t.string,
    classificationAdjustments: t.string,
    files: t.readonlyArray(submitWireFileCodec),
});

const traceCodec = t.type({
    traceId: traceIdCodec,
});

const jobCountsCodec = t.type({
    submittedFiles: t.number,
    previewRows: t.number,
});

const artifactCodec = t.type({
    id: artifactIdCodec,
    kind: t.string,
    fileName: nullable(workbookFileNameCodec),
    mediaType: nullable(mediaTypeCodec),
    byteSize: nullable(fileSizeCodec),
});

const jobPhaseCodec = t.union([
    t.literal("Accepted"),
    t.literal("Staging"),
    t.literal("Extracting"),
    t.literal("Classifying"),
    t.literal("WritingWorkbook"),
    t.literal("Completed"),
    t.literal("Failed"),
]);

const jobStatusCodec = t.type({
    jobId: jobIdCodec,
    phase: jobPhaseCodec,
    failure: nullable(failureMessageCodec),
    counts: nullable(jobCountsCodec),
    workbook: nullable(artifactCodec),
});

const submitJobResultCodec = t.type({
    jobId: jobIdCodec,
    status: jobStatusCodec,
    trace: traceCodec,
});

const statusResultCodec = t.type({
    status: jobStatusCodec,
    trace: traceCodec,
});

const previewRowCodec = t.type({
    fileName: fileNameCodec,
    date: dateCodec,
    description: descriptionCodec,
    amount: nullable(amountCodec),
    classification: classificationCodec,
    confidence: confidenceCodec,
    reasoning: nullable(reasoningCodec),
});

const previewResultCodec = t.type({
    rows: t.readonlyArray(previewRowCodec),
    trace: traceCodec,
});

const errorCodeCodec = t.union([
    t.literal("invalidPayload"),
    t.literal("invalidJobId"),
    t.literal("unknownJob"),
    t.literal("workbookUnavailable"),
    t.literal("serviceUnavailable"),
    t.literal("unsupportedFileType"),
    t.literal("noExtractableText"),
    t.literal("processingFailed"),
    t.literal("downloadUnavailable"),
    t.literal("networkError"),
    t.literal("responseDecodeError"),
]);

const clientErrorCodec = t.type({
    error: errorCodeCodec,
    message: refusalMessageCodec,
    trace: nullable(traceCodec),
});

const fileInputAvailable = (value: unknown): value is File =>
    typeof File !== "undefined" && value instanceof File;

const fileListInputAvailable = (value: unknown): value is File[] =>
    Array.isArray(value) && value.length > 0 && value.every(fileInputAvailable);

const decodeWith = <A>(codec: t.Decoder<unknown, A>, input: unknown): StatementClassifierApiDecodeResult<A> => {
    const decoded = codec.decode(input);
    if (E.isRight(decoded)) {
        return {
            type: "ok",
            value: decoded.right,
        };
    }

    return {
        type: "error",
        message: PathReporter.report(decoded).join("; "),
    };
};

export const statementClassifierLocalError = (
    error: StatementClassifierErrorCode,
    message: StatementClassifierRefusalMessage,
): StatementClassifierClientError => ({
    error,
    message,
    trace: Option.none(),
});

export const decodeStatementClassifierGuidance = (
    input: unknown,
): StatementClassifierApiDecodeResult<StatementClassifierGuidance> => decodeWith(guidanceCodec, input);

export const decodeStatementClassifierSubmittedFile = (
    input: unknown,
): StatementClassifierApiDecodeResult<StatementClassifierSubmittedFile> => decodeWith(submittedFileCodec, input);

export const decodeStatementClassifierSubmitJobRequest = (
    input: unknown,
): StatementClassifierApiDecodeResult<StatementClassifierSubmitJobRequest> => {
    const decoded = decodeWith(
        t.type({
            files: new t.Type<File[], File[], unknown>(
                "StatementClassifierBrowserFiles",
                fileListInputAvailable,
                (value, context) => (fileListInputAvailable(value) ? t.success(value) : t.failure(value, context)),
                t.identity,
            ),
            guidance: guidanceCodec,
        }),
        input,
    );

    return decoded;
};

export const decodeStatementClassifierSubmitWireRequest = (
    input: unknown,
): StatementClassifierApiDecodeResult<StatementClassifierSubmitWireRequest> =>
    decodeWith(submitWireRequestCodec, input);

export const decodeStatementClassifierSubmitJobResponse = (
    input: unknown,
): StatementClassifierApiDecodeResult<StatementClassifierSubmitJobResult> =>
    decodeWith(submitJobResultCodec, input);

export const decodeStatementClassifierStatusResponse = (
    input: unknown,
): StatementClassifierApiDecodeResult<StatementClassifierStatusResult> =>
    decodeWith(statusResultCodec, input);

export const decodeStatementClassifierPreviewResponse = (
    input: unknown,
): StatementClassifierApiDecodeResult<StatementClassifierPreviewResult> =>
    decodeWith(previewResultCodec, input);

export const decodeStatementClassifierRefusal = (
    input: unknown,
): StatementClassifierApiDecodeResult<StatementClassifierClientError> => decodeWith(clientErrorCodec, input);

export const decodeStatementClassifierWorkbookFileName = (
    input: string,
): StatementClassifierApiDecodeResult<StatementClassifierWorkbookFileName> => decodeWith(workbookFileNameCodec, input);

export interface StatementClassifierClient {
    submitJob(
        files: File[],
        guidance: StatementClassifierGuidance,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierSubmitJobResult>;

    getJobStatus(
        jobId: StatementClassifierJobId,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierStatusResult>;

    getPreview(
        jobId: StatementClassifierJobId,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierPreviewResult>;

    downloadWorkbook(
        jobId: StatementClassifierJobId,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierWorkbookResult>;
}
